// Centralized invoice display rules and helper logic

import i18n from '../../../constants/languages';

export interface InvoiceDisplayRule {
  countryCode: string;               
  currencyRequired?: string;          // Currency prerequisite (e.g. THB). If undefined, ignore.
  transportTypeRequired?: string;     
  excludedCategories?: string[];      // Categories where invoice must be hidden.
  excludedWhenTransportTypes?: string[]; // Transport types that cause hide when coupled with a category (TH special case with category 4)
  categoryForTransportFilter?: string;   // Category within which transport filtering applies (e.g. '4').
  excludedCompanyCodes?: string[];    // Company codes where invoice must be hidden (e.g. MY98).
  extraHidePredicate?: (inputValues: Record<string, any>, i: number) => boolean; // Advanced hook for country specific logic.
  forceShowPredicate?: (inputValues: Record<string, any>, i: number) => boolean; // Hook to force show regardless of above.
}

// Thailand rule extracted from legacy loadInvoice_Number logic.
// Notes:
// - Currency must be THB
// - Hide for categories: 16 (Per diem), 29 (Other employee benefits detail types 1,3,4 only), 41, 42, 46, 47, 48
// - Hide for category 4 when transport type in set (1,2,3,5,9,10)
export const TH_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: 'TH',
  currencyRequired: 'THB',
  categoryForTransportFilter: '4',
  excludedWhenTransportTypes: ['1','2','3','5','9','10'],
  excludedCategories: ['16','41','42','46','47','48'],
  extraHidePredicate: (vals, i) => {
    const rawCat = vals[`ddlCategory${i}`];
    const cat = rawCat !== undefined && rawCat !== null ? String(rawCat) : '';
    
    if (cat === '29') { // Detail type filter inside cat 29
      const detail_raw = vals[`ddlDetailType${i}`];
      const detail = detail_raw !== undefined && detail_raw !== null ? String(detail_raw) : '';
      // Original intent: hide invoice for category 29 when detail types are 1,3,4.
      if (['1','3','4'].includes(detail)) return true; // hide condition
    }
    return false;
  }
};

// Registry for all invoice rules
// Cambodia rule (KH): currently shows invoice by default except for a few excluded categories (example placeholders).
// Adjust excludedCategories when real KH exclusions are known.
export const KH_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: 'KH',
  currencyRequired: 'USD',
  excludedCategories: ['16','48'] // Replace with actual category IDs to hide
};

// Begin, Saber, 20260514, SR-959286, CHN-20860, Load invoice number for MY (except MY98)
export const MY_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: 'MY',
  excludedCompanyCodes: ['MY98'],
};

// Begin, Saber, 20260519, CHN-21547, Load invoice number for BN
export const BN_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: 'BN',
};
// End, Saber, 20260519, CHN-21547, Load invoice number for BN

// Begin, Saber, 20260731, Enable invoice number for CN
export const CN_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: 'CN',
};
// End, Saber, 20260731, Enable invoice number for CN

// Begin,Saber,20260922,ECL-713 - Enable Invoice/Receipt Number globally. Thailand keeps TH_INVOICE_RULE; MY keeps the MY98 exception.
export const DEFAULT_INVOICE_RULE: InvoiceDisplayRule = {
  countryCode: '*',
};
// End,Saber,20260922,ECL-713

export const INVOICE_RULES: InvoiceDisplayRule[] = [TH_INVOICE_RULE, KH_INVOICE_RULE, MY_INVOICE_RULE, BN_INVOICE_RULE, CN_INVOICE_RULE];
// End, Saber, 20260514, SR-959286, CHN-20860

function matchesCountry(rule: InvoiceDisplayRule, countryCode: string) {
  return rule.countryCode === countryCode;
}

// Begin,Saber,20260922,ECL-713 - Market length, mandatory, and duplicate rules for Invoice/Receipt Number.
export interface InvoiceFieldRule {
  maxLength: number;
  mandatory: boolean;
  allowDuplicates: boolean;
}

const INVOICE_FIELD_RULES: Record<string, InvoiceFieldRule> = {
  CN: { maxLength: 25, mandatory: true, allowDuplicates: true },
  TH: { maxLength: 16, mandatory: true, allowDuplicates: false },
  MY: { maxLength: 16, mandatory: true, allowDuplicates: true },
  BN: { maxLength: 16, mandatory: true, allowDuplicates: true },
  KH: { maxLength: 16, mandatory: true, allowDuplicates: true },
};

const DEFAULT_INVOICE_FIELD_RULE: InvoiceFieldRule = {
  maxLength: 16,
  mandatory: true,
  allowDuplicates: true,
};

export function getInvoiceFieldRule(countryCode?: string): InvoiceFieldRule {
  return INVOICE_FIELD_RULES[countryCode ?? ''] ?? DEFAULT_INVOICE_FIELD_RULE;
}

export function getInvoiceNumberMaxLength(countryCode?: string): number {
  return getInvoiceFieldRule(countryCode).maxLength;
}
// End,Saber,20260922,ECL-713

export function shouldShowInvoiceNumber(i: number, inputValues: Record<string, any>, countryCode: string, companyCode?: string, overrideShow?: boolean): boolean {
  // Begin,Saber,20260922,ECL-713 - Show globally by default. Thailand continues to use its existing hide rules.
  const rule = INVOICE_RULES.find(r => matchesCountry(r, countryCode)) ?? DEFAULT_INVOICE_RULE;
  // End,Saber,20260922,ECL-713

  // Company code exclusion — always applies (MY98 never shows Receipt Number)
  // Saber, CHN-20860 Receipt number for MY(Except MY98) V3
  if (companyCode && rule.excludedCompanyCodes?.includes(companyCode)) return false;

  // Force show override
  if (rule.forceShowPredicate && rule.forceShowPredicate(inputValues, i)) return true;

  // Currency check
  if (rule.currencyRequired) {
    const cur = inputValues[`ddlCurrency${i}`];
    if (cur !== rule.currencyRequired) return false;
  }
    const rawCategory = inputValues[`ddlCategory${i}`];
    const category = rawCategory !== undefined && rawCategory !== null
    ? String(rawCategory)
    : '';  
  const rawTransportType = inputValues[`ddlTransportType${i}`];
  const transportType = rawTransportType !== undefined && rawTransportType !== null
    ? String(rawTransportType)
    : '';


     const detail_raw2 = inputValues[`ddlDetailType${i}`];

  // Category-based exclusions
  if (rule.excludedCategories?.includes(category)) return false;

  // Transport filtering inside a specific category
  if (rule.categoryForTransportFilter && category === rule.categoryForTransportFilter && rule.excludedWhenTransportTypes?.includes(transportType)) {
    return false;
  }

  // Extra predicate for complex hide logic
  if (rule.extraHidePredicate && rule.extraHidePredicate(inputValues, i)) return false;

  // Passed all hide checks -> show
  return true;
}

export function applyInvoiceVisibility(
  i: number,
  inputValues: Record<string, any>,
  countryCode: string,
  handleInputChange: (id: string, value?: any) => Promise<void>,
  elementDisplay: (selector: string, show: boolean, type: string) => void,
  mandatoryInput: (selector: string, required: boolean, type: string) => void,
  objAttachment?: any,
  companyCode?: string,
  overrideShow?: boolean
) {
  const show = shouldShowInvoiceNumber(i, inputValues, countryCode, companyCode, overrideShow);
  elementDisplay(`.divTHB`, show, 'div');
  mandatoryInput(`#txtInvoiceNum${i}`, show, 'text');

  if (!show) {
    handleInputChange(`txtInvoiceNum${i}`, '');
    return;
  }

  // When showing, optionally bind existing value from attachment
  handleInputChange(`hidValidate_InvoiceNum${i}`, 1);
  if (objAttachment?.invoice_number) {
    handleInputChange(`txtInvoiceNum${i}`, objAttachment.invoice_number);
  }
}

// Label text for the invoice field. Kept as a pure function so React can render it
// on first paint. The DOM updater below is only for callers that still patch the label.
export function getInvoiceNumberLabel(countryCode?: string): string {
  switch (countryCode) {
    case 'KH':
      return 'Invoice/Receipt number';
    case 'MY':
    case 'BN':
      return 'Invoice/Receipt Number';
    case 'CN':
      return i18n.language === 'cn'
        ? i18n.t('Invoice Number')
        : 'Invoice/Receipt Number';
    case 'TH':
      return 'Invoice Number';
    default:
      // ECL-568: markets without a dedicated label, including AU.
      return 'Invoice/Receipt Number';
  }
}

// Update the invoice label text based on country specific wording (e.g., KH wants "Invoice/Receipt number").
// Expects the label element to have id="i" within the invoice number block.
export function updateInvoiceLabel(countryCode: string) {
  try {
    const labelEl = document.querySelector<HTMLElement>('label#i[data-tag="Invoice_number"]');
    if (!labelEl) return;
    labelEl.innerText = getInvoiceNumberLabel(countryCode);
  } catch {
    // swallow errors silently; DOM may not be ready yet
  }
}
